import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  RadialBarChart,
  RadialBar,
  Cell,
} from 'recharts';
import { Trophy, Check, X, AlertCircle, Target, Download, PieChart as PieChartIcon, ListChecks, ArrowLeft, ChevronDown, ChevronUp, Info, FileText, FileDown } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import TTSControls from './TTSControls';

const dedupeOptions = (options = []) => {
  const seen = new Set();
  const result = [];
  for (const option of options) {
    if (!option) continue;
    const labelKey = (option.option_label || '').trim().toUpperCase();
    const fallbackKey = `${(option.option_text || '').trim().toLowerCase()}|${option.id ?? ''}`;
    const key = labelKey || fallbackKey;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(option);
  }
  return result;
};

const toSafeFilename = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase() || 'area';

const QUESTION_FILTERS = [
  { id: 'all', label: 'Todas' },
  { id: 'correct', label: 'Correctas' },
  { id: 'incorrect', label: 'Incorrectas' },
  { id: 'skipped', label: 'Sin responder' },
  { id: 'flagged', label: 'Marcadas' },
];

const emptyAnalytics = {
  summary: {
    totalQuestions: 0,
    correct: 0,
    incorrect: 0,
    skipped: 0,
    accuracy: 0,
  },
  areas: [],
  organSystems: [],
  topics: [],
  subtopics: [],
  subtopicSpecific: [],
  questionSummaries: [],
};

const formatDuration = (seconds) => {
  const safe = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const matchesFilter = (status, filterId, flagged) => {
  switch (filterId) {
    case 'correct':
      return status === 'correct';
    case 'incorrect':
      return status === 'incorrect';
    case 'skipped':
      return status === 'skipped';
    case 'flagged':
      return !!flagged;
    default:
      return true;
  }
};

function SimulationResultsPanel({
  darkMode,
  finishPromptOverlay = null,
  analytics,
  timeSpentSeconds = 0,
  allocatedSeconds = 0,
  onReset,
  onBack,
  fromGenerator = false,
  downloadComponent = null,
  headerMeta = null,
}) {
  const hasAnalytics = Boolean(analytics);
  const safeAnalytics = useMemo(() => analytics || emptyAnalytics, [analytics]);
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  const {
    summary,
    areas,
    organSystems,
    topics,
    subtopics,
    subtopicSpecific,
    questionSummaries,
  } = safeAnalytics;

  const [selectedAreaKey, setSelectedAreaKey] = useState(() => (areas[0]?.key ?? null));
  const [selectedTopicKey, setSelectedTopicKey] = useState(null);
  const [questionReviewFilter, setQuestionReviewFilter] = useState('all');
  const [drilldownAreaKey, setDrilldownAreaKey] = useState(null);
  const [distributionViewMode, setDistributionViewMode] = useState('vertical');
  const [showDistributionExpanded, setShowDistributionExpanded] = useState(false);

  useEffect(() => {
    if (areas.length === 0) {
      setSelectedAreaKey(null);
      return;
    }
    setSelectedAreaKey((prev) => (prev && areas.some((area) => area.key === prev) ? prev : areas[0].key));
  }, [areas]);

  useEffect(() => {
    setSelectedTopicKey(null);
  }, [selectedAreaKey]);

  useEffect(() => {
    setQuestionReviewFilter('all');
  }, [selectedAreaKey, selectedTopicKey]);

  useEffect(() => {
    if (!drilldownAreaKey) return;
    const exists = areas.some((area) => area.key === drilldownAreaKey);
    if (!exists) {
      setDrilldownAreaKey(null);
    }
  }, [areas, drilldownAreaKey]);

  const toggleAreaSelection = useCallback((areaKey) => {
    setSelectedAreaKey((prev) => (prev === areaKey ? null : areaKey));
  }, []);

  const handleOpenAreaDrilldown = useCallback((areaKey) => {
    if (!areaKey) return;
    setSelectedAreaKey(areaKey);
    setDrilldownAreaKey(areaKey);
  }, []);

  const handleCloseAreaDrilldown = useCallback(() => {
    setDrilldownAreaKey(null);
  }, []);

  const palette = useMemo(
    () => [
      '#22d3ee',
      '#818cf8',
      '#facc15',
      '#f472b6',
      '#34d399',
      '#f97316',
      '#38bdf8',
      '#a855f7',
      '#fb7185',
      '#4ade80',
    ],
    []
  );

  const areaLookup = useMemo(() => {
    const lookup = {};
    areas.forEach((area) => {
      lookup[area.key] = area;
    });
    return lookup;
  }, [areas]);

  const selectedArea = selectedAreaKey ? areaLookup[selectedAreaKey] || null : null;

  const topicLookup = useMemo(() => {
    if (!selectedArea) return {};
    const lookup = {};
    (selectedArea.topics || []).forEach((topic) => {
      lookup[topic.key] = topic;
    });
    return lookup;
  }, [selectedArea]);

  const selectedTopic = selectedTopicKey ? topicLookup[selectedTopicKey] || null : null;

  const filteredReviewQuestions = useMemo(
    () =>
      questionSummaries.filter((item) => {
        if (selectedAreaKey && item.area !== selectedAreaKey) return false;
        if (selectedTopicKey && item.topic !== selectedTopicKey) return false;
        return matchesFilter(item.status, questionReviewFilter, item.flagged);
      }),
    [questionSummaries, selectedAreaKey, selectedTopicKey, questionReviewFilter]
  );

  const areaRows = useMemo(
    () =>
      areas.map((area) => ({
        key: area.key,
        label: area.label,
        total: area.total,
        correct: area.correct,
        errors: area.incorrect,
        accuracy: area.accuracy,
      })),
    [areas]
  );

  const organRows = useMemo(
    () =>
      organSystems.map((entry) => ({
        key: entry.key,
        label: entry.label,
        total: entry.total,
        correct: entry.correct,
        errors: entry.incorrect,
        accuracy: entry.accuracy,
      })),
    [organSystems]
  );

  const topicRows = useMemo(
    () =>
      topics.map((entry) => ({
        key: entry.key,
        label: entry.label,
        total: entry.total,
        correct: entry.correct,
        errors: entry.incorrect,
        accuracy: entry.accuracy,
      })),
    [topics]
  );

  const subtopicRows = useMemo(
    () =>
      subtopics.map((entry) => ({
        key: entry.key,
        label: entry.label,
        total: entry.total,
        correct: entry.correct,
        errors: entry.incorrect,
        accuracy: entry.accuracy,
      })),
    [subtopics]
  );

  const detailRows = useMemo(
    () =>
      subtopicSpecific.map((entry) => ({
        key: entry.key,
        label: entry.label,
        total: entry.total,
        correct: entry.correct,
        errors: entry.incorrect,
        accuracy: entry.accuracy,
      })),
    [subtopicSpecific]
  );

  const dimensionOptions = [
    { id: 'area', label: 'Áreas', rows: areaRows },
    { id: 'organ', label: 'Sistemas', rows: organRows },
    { id: 'topic', label: 'Temas', rows: topicRows },
    { id: 'subtopic', label: 'Subtemas', rows: subtopicRows },
    { id: 'subtopicSpecific', label: 'Detalle', rows: detailRows },
  ];

  const handleTopicFocus = useCallback(
    (topicKey) => {
      if (!topicKey) return;
      setSelectedTopicKey((prev) => (prev === topicKey ? null : topicKey));
    },
    []
  );

  const drilldownArea = useMemo(
    () => areas.find((area) => area.key === drilldownAreaKey) || null,
    [areas, drilldownAreaKey]
  );

  const formatNumber = useCallback((value) => {
    const num = Number.isFinite(value) ? value : 0;
    return num.toLocaleString('es-PE');
  }, []);

  const formatPercent = useCallback((value) => {
    if (!Number.isFinite(value)) return '0%';
    return `${Math.round(value)}%`;
  }, []);

  const handleAreaKeyDown = useCallback(
    (event, areaKey) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleOpenAreaDrilldown(areaKey);
      } else if (event.key === ' ') {
        event.preventDefault();
        toggleAreaSelection(areaKey);
      }
    },
    [handleOpenAreaDrilldown, toggleAreaSelection]
  );

  const [resultsViewDimension, setResultsViewDimension] = useState('area');
  const breakdownRows = dimensionOptions.find((option) => option.id === resultsViewDimension)?.rows || [];

  const weaknesses = useMemo(
    () =>
      [...areas]
        .sort((a, b) => {
          if (b.incorrect !== a.incorrect) return b.incorrect - a.incorrect;
          if (b.total !== a.total) return b.total - a.total;
          return a.label.localeCompare(b.label, 'es');
        })
        .slice(0, 3),
    [areas]
  );

  const summaryCards = useMemo(() => {
    const baseCards = [
      {
        id: 'score',
        label: 'Puntaje obtenido',
        icon: Trophy,
        primary: `${Number.isFinite(summary.accuracy) ? Math.round(summary.accuracy) : 0}%`,
        secondary: `${summary.correct} correctas de ${summary.totalQuestions} preguntas`,
        gradient: darkMode
          ? 'bg-gradient-to-br from-cyan-600/30 via-blue-600/20 to-purple-600/20 border border-cyan-500/40'
          : 'bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-500 text-white shadow-sky-200',
      },
      {
        id: 'correct',
        label: 'Correctas',
        icon: Check,
        primary: summary.correct,
        secondary:
          areas.length > 0 ? `Mejor área: ${areas[0].label} (${areas[0].accuracy}%)` : 'Buen trabajo, ¡sigue así!',
        gradient: darkMode
          ? 'bg-slate-900/70 border-slate-800 text-slate-100'
          : 'bg-white border-slate-200 text-slate-700',
      },
      {
        id: 'incorrect',
        label: 'Incorrectas',
        icon: X,
        primary: summary.incorrect,
        secondary:
          weaknesses.length > 0
            ? `Revisa ${weaknesses[0].label} (${weaknesses[0].incorrect} errores)`
            : 'Sin áreas críticas detectadas',
        gradient: darkMode
          ? 'bg-slate-900/70 border-slate-800 text-slate-100'
          : 'bg-white border-slate-200 text-slate-700',
      },
      {
        id: 'skipped',
        label: 'Sin responder',
        icon: AlertCircle,
        primary: summary.skipped,
        secondary: `Tiempo utilizado: ${formatDuration(timeSpentSeconds)}`,
        gradient: darkMode
          ? 'bg-slate-900/70 border-slate-800 text-slate-100'
          : 'bg-white border-slate-200 text-slate-700',
      },
    ];
    return baseCards;
  }, [areas, summary, weaknesses, darkMode, timeSpentSeconds]);

  const headerInfo = useMemo(() => {
    if (!headerMeta) return null;
    const { title, date, time } = headerMeta;
    const hasMeta = title || date || time;
    if (!hasMeta) return null;
    return (
      <div
        className={`rounded-2xl border px-5 py-4 ${
          darkMode ? 'border-slate-800 bg-slate-900/70 text-slate-100' : 'border-slate-200 bg-white text-slate-700'
        }`}
      >
        <div className="flex flex-wrap justify-between items-center gap-4 text-sm">
          <span className="font-semibold">{title}</span>
          <span>
            {date && <span className="mr-2">{date}</span>}
            {time && <span>{time}</span>}
          </span>
        </div>
      </div>
    );
  }, [headerMeta, darkMode]);

  const selectedAreaLabel = selectedArea?.label || 'Todas las áreas';
  const selectedTopicLabel = selectedTopic?.label || 'Todos los temas';

  const averageTimePerQuestion = summary.totalQuestions
    ? formatDuration(timeSpentSeconds / summary.totalQuestions)
    : '00:00';

  const areaOverviewData = useMemo(
    () =>
      areas.map((area, index) => ({
        name: area.label,
        accuracy: Math.round(area.accuracy || 0),
        incorrect: area.incorrect,
        correct: area.correct,
        skipped: area.skipped,
        key: area.key,
        fill: palette[index % palette.length],
      })),
    [areas, palette]
  );

  const stackedAreaData = useMemo(
    () =>
      areas.map((area) => ({
        area: area.label,
        correct: area.correct,
        incorrect: area.incorrect,
        skipped: area.skipped,
        accuracy: Math.round(area.accuracy || 0),
      })),
    [areas]
  );

  const distributionTotals = useMemo(() => {
    const base = areas.reduce(
      (acc, area) => {
        acc.total += area.total || 0;
        acc.correct += area.correct || 0;
        acc.incorrect += area.incorrect || 0;
        const skippedValue =
          typeof area.skipped === 'number' ? area.skipped : Math.max((area.total || 0) - (area.correct || 0) - (area.incorrect || 0), 0);
        acc.skipped += skippedValue;
        return acc;
      },
      { total: 0, correct: 0, incorrect: 0, skipped: 0 }
    );

    const safeTotal = base.total || Math.max(base.correct + base.incorrect + base.skipped, 1);
    return {
      ...base,
      accuracy: safeTotal > 0 ? Math.round((base.correct / safeTotal) * 100) : 0,
      incorrectPercentage: safeTotal > 0 ? Math.round((base.incorrect / safeTotal) * 100) : 0,
      skippedPercentage: safeTotal > 0 ? Math.round((base.skipped / safeTotal) * 100) : 0,
    };
  }, [areas]);

  const distributionPercentData = useMemo(
    () =>
      stackedAreaData.map((entry) => {
        const total = Math.max(entry.correct + entry.incorrect + entry.skipped, 1);
        return {
          ...entry,
          correctPct: (entry.correct / total) * 100,
          incorrectPct: (entry.incorrect / total) * 100,
          skippedPct: (entry.skipped / total) * 100,
        };
      }),
    [stackedAreaData]
  );

  const distributionViewOptions = useMemo(
    () => [
      { id: 'vertical', label: 'Barras apiladas' },
      { id: 'horizontal', label: 'Apiladas horizontales' },
      { id: 'grouped', label: 'Barras agrupadas' },
      { id: 'percent', label: '100% stacked' },
    ],
    []
  );

  const renderDistributionChart = useCallback(() => {
    const tooltipStyle = {
      backgroundColor: darkMode ? '#0f172a' : '#ffffff',
      borderColor: darkMode ? '#1f2937' : '#e5e7eb',
      color: darkMode ? '#e2e8f0' : '#1f2937',
    };

    switch (distributionViewMode) {
      case 'horizontal':
        return (
          <BarChart
            data={stackedAreaData}
            layout="vertical"
            margin={{ top: 10, right: 24, bottom: 10, left: 120 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e5e7eb'} />
            <XAxis type="number" allowDecimals={false} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
            <YAxis type="category" dataKey="area" stroke={darkMode ? '#cbd5f5' : '#1f2937'} width={120} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar dataKey="correct" stackId="stack" fill={darkMode ? '#22c55e' : '#16a34a'} name="Correctas" />
            <Bar dataKey="incorrect" stackId="stack" fill={darkMode ? '#f97316' : '#ef4444'} name="Incorrectas" />
            <Bar dataKey="skipped" stackId="stack" fill={darkMode ? '#38bdf8' : '#6366f1'} name="Sin responder" />
          </BarChart>
        );
      case 'grouped':
        return (
          <BarChart data={stackedAreaData} margin={{ top: 10, right: 24, bottom: 30, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e5e7eb'} />
            <XAxis dataKey="area" stroke={darkMode ? '#cbd5f5' : '#1f2937'} interval={0} angle={-25} textAnchor="end" height={80} />
            <YAxis allowDecimals={false} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar dataKey="correct" fill={darkMode ? '#22c55e' : '#16a34a'} name="Correctas" />
            <Bar dataKey="incorrect" fill={darkMode ? '#f97316' : '#ef4444'} name="Incorrectas" />
            <Bar dataKey="skipped" fill={darkMode ? '#38bdf8' : '#6366f1'} name="Sin responder" />
          </BarChart>
        );
      case 'percent':
        return (
          <BarChart data={distributionPercentData} margin={{ top: 10, right: 24, bottom: 30, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e5e7eb'} />
            <XAxis dataKey="area" stroke={darkMode ? '#cbd5f5' : '#1f2937'} interval={0} angle={-25} textAnchor="end" height={80} />
            <YAxis domain={[0, 100]} stroke={darkMode ? '#cbd5f5' : '#1f2937'} tickFormatter={(value) => `${value}%`} />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name) => [`${value.toFixed(1)}%`, name === 'correctPct' ? 'Correctas' : name === 'incorrectPct' ? 'Incorrectas' : 'Sin responder']}
            />
            <Legend formatter={(value) => (value === 'correctPct' ? 'Correctas' : value === 'incorrectPct' ? 'Incorrectas' : 'Sin responder')} />
            <Bar dataKey="correctPct" stackId="percent" fill={darkMode ? '#22c55e' : '#16a34a'} name="Correctas" />
            <Bar dataKey="incorrectPct" stackId="percent" fill={darkMode ? '#f97316' : '#ef4444'} name="Incorrectas" />
            <Bar dataKey="skippedPct" stackId="percent" fill={darkMode ? '#38bdf8' : '#6366f1'} name="Sin responder" />
          </BarChart>
        );
      case 'vertical':
      default:
        return (
          <BarChart data={stackedAreaData} margin={{ top: 10, right: 24, bottom: 30, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e5e7eb'} />
            <XAxis dataKey="area" stroke={darkMode ? '#cbd5f5' : '#1f2937'} interval={0} angle={-25} textAnchor="end" height={80} />
            <YAxis allowDecimals={false} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar dataKey="correct" stackId="stack" fill={darkMode ? '#22c55e' : '#16a34a'} name="Correctas" />
            <Bar dataKey="incorrect" stackId="stack" fill={darkMode ? '#f97316' : '#ef4444'} name="Incorrectas" />
            <Bar dataKey="skipped" stackId="stack" fill={darkMode ? '#38bdf8' : '#6366f1'} name="Sin responder" />
          </BarChart>
        );
    }
  }, [darkMode, distributionPercentData, distributionViewMode, stackedAreaData]);
  const selectedAreaTopics = useMemo(() => selectedArea?.topics || [], [selectedArea]);

  const topicPerformanceData = useMemo(
    () =>
      selectedAreaTopics.map((topic, index) => {
        const skipped =
          typeof topic.skipped === 'number'
            ? topic.skipped
            : Math.max(topic.total - topic.correct - topic.incorrect, 0);
        return {
          key: topic.key,
          label: topic.label,
          total: topic.total,
          correct: topic.correct,
          incorrect: topic.incorrect,
          skipped,
          accuracy: Math.round(topic.accuracy || 0),
          fill: palette[index % palette.length],
        };
      }),
    [selectedAreaTopics, palette]
  );

  const topicHeatmap = useMemo(() => {
    if (!selectedArea) return [];
    return topicPerformanceData.map((topic) => ({
      topic: topic.label,
      accuracy: topic.accuracy,
      incorrect: topic.incorrect,
      total: topic.total,
    }));
  }, [selectedArea, topicPerformanceData]);

  const selectedTopicData = useMemo(
    () => selectedAreaTopics.find((topic) => topic.key === selectedTopicKey) || null,
    [selectedAreaTopics, selectedTopicKey]
  );

  const selectedTopicSubtopics = useMemo(
    () =>
      (selectedTopicData?.subtopics || []).map((subtopic) => ({
        ...subtopic,
        accuracy: Math.round(subtopic.accuracy || 0),
        skipped:
          typeof subtopic.skipped === 'number'
            ? subtopic.skipped
            : Math.max(subtopic.total - subtopic.correct - subtopic.incorrect, 0),
      })),
    [selectedTopicData]
  );

  const coachingInsights = useMemo(() => {
    const sortedByAccuracy = [...areas].sort((a, b) => (a.accuracy || 0) - (b.accuracy || 0));
    const weakest = sortedByAccuracy.slice(0, 3);
    const strongest = [...areas]
      .sort((a, b) => (b.accuracy || 0) - (a.accuracy || 0))
      .slice(0, 2);

    const insights = [];

    if (weakest.length) {
      insights.push(
        `Prioriza ${weakest[0].label}: ${weakest[0].incorrect} errores sobre ${weakest[0].total} preguntas (precisión ${weakest[0].accuracy}%).`
      );
    }
    if (weakest.length > 1) {
      insights.push(
        `Refuerza ${weakest[1].label}; tu precisión cayó a ${weakest[1].accuracy}% con ${weakest[1].incorrect} respuestas incorrectas.`
      );
    }
    if (strongest.length) {
      insights.push(`Mantén tu fortaleza en ${strongest[0].label} (precisión ${strongest[0].accuracy}%).`);
    }
    if (selectedArea) {
      const topTopic =
        (selectedArea.topics || [])
          .filter((topic) => topic.total > 0)
          .sort((a, b) => (b.incorrect || 0) - (a.incorrect || 0))[0] || null;
      if (topTopic) {
        insights.push(
          `Dentro de ${selectedArea.label}, enfócate en ${topTopic.label}: ${topTopic.incorrect} errores de ${topTopic.total} preguntas.`
        );
      }
    }
    if (!insights.length) {
      insights.push('Excelente equilibrio general. Continúa practicando para mantener la consistencia.');
    }
    return insights;
  }, [areas, selectedArea]);

  const handleExportPNG = useCallback(async () => {
    if (!dashboardRef.current || isExporting) return;
    try {
      setIsExporting(true);
      const canvas = await html2canvas(dashboardRef.current, {
        backgroundColor: darkMode ? '#0f172a' : '#ffffff',
        scale: 2,
        logging: false,
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `mediexampro_reporte_${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
    } catch (error) {
      console.error('Error exporting dashboard:', error);
    } finally {
      setIsExporting(false);
    }
  }, [darkMode, isExporting]);

  if (!hasAnalytics) {
    return (
      <>
        {finishPromptOverlay}
        <div className="max-w-4xl mx-auto p-6">
          <div
            className={`rounded-3xl border py-16 text-center ${
              darkMode ? 'bg-slate-900 border-slate-800 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
            }`}
          >
            <p className="text-lg font-semibold">Generando analítica del simulacro...</p>
          </div>
        </div>
      </>
    );
  }

  if (drilldownArea) {
    return (
      <>
        {finishPromptOverlay}
        <AreaDrilldownView
          area={drilldownArea}
          darkMode={darkMode}
          onBack={handleCloseAreaDrilldown}
          palette={palette}
          formatNumber={formatNumber}
          formatPercent={formatPercent}
          questionSummaries={questionSummaries}
        />
      </>
    );
  }

  return (
    <>
      {finishPromptOverlay}
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8" ref={dashboardRef}>
        {headerInfo}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
              Supervisión de desempeño por áreas
            </h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Explora las áreas para profundizar en temas, subtemas y preguntas específicas.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleExportPNG}
              disabled={isExporting}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow ${
                darkMode
                  ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 hover:bg-cyan-500/30'
                  : 'bg-sky-100 text-sky-700 border border-sky-200 hover:bg-sky-200'
              } ${isExporting ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Generando...' : 'Exportar reporte (PNG)'}
            </button>
            {downloadComponent}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ id, label, icon: Icon, primary, secondary, gradient }) => (
            <div key={id} className={`rounded-3xl p-6 shadow-lg ${gradient}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium opacity-80">{label}</span>
                <Icon className={`w-6 h-6 ${darkMode ? 'text-cyan-200' : 'text-slate-600'}`} />
              </div>
              <p className="mt-4 text-4xl font-bold tracking-tight">{primary}</p>
              <p className={`mt-3 text-sm ${darkMode ? 'text-slate-300/80' : 'text-slate-600'}`}>{secondary}</p>
            </div>
          ))}
        </div>

        <div
          className={`rounded-3xl border p-6 space-y-6 ${
            darkMode ? 'bg-slate-900/70 border-slate-800 text-slate-100' : 'bg-white border-slate-200 text-slate-700'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Target className={darkMode ? 'text-cyan-300' : 'text-sky-500'} />
                Panorama general por áreas
              </h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Selecciona un área para profundizar en sus temas y preguntas.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Precisión por área</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {areaOverviewData.map((area) => {
              const isActive = selectedAreaKey === area.key;
              return (
                <button
                  key={area.key}
                  type="button"
                  onClick={() => toggleAreaSelection(area.key)}
                  onDoubleClick={() => handleOpenAreaDrilldown(area.key)}
                  onKeyDown={(event) => handleAreaKeyDown(event, area.key)}
                  title="Doble clic o Enter para abrir el reporte detallado"
                  className={`text-left rounded-3xl border p-5 transition-all shadow ${
                    isActive
                      ? darkMode
                        ? 'border-cyan-400/70 bg-cyan-500/10 shadow-[0_0_25px_rgba(34,211,238,0.2)]'
                        : 'border-sky-400 bg-sky-50 shadow-sky-200/80'
                      : darkMode
                      ? 'border-slate-800 bg-slate-900/60 hover:border-cyan-400/40'
                      : 'border-slate-200 bg-white hover:border-sky-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h4 className="text-lg font-semibold">{area.name}</h4>
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {area.correct} aciertos · {area.incorrect} errores · {area.skipped} sin responder
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold ${
                        darkMode ? 'bg-slate-800 text-cyan-200' : 'bg-sky-100 text-sky-700'
                      }`}
                    >
                      {area.accuracy}%
                    </span>
                  </div>
                  <div className="mt-4 h-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart
                        data={[area]}
                        innerRadius="40%"
                        outerRadius="100%"
                        startAngle={90}
                        endAngle={90 - (360 * Math.min(Math.max(area.accuracy, 0), 100)) / 100}
                      >
                        <RadialBar
                          background
                          dataKey="accuracy"
                          cornerRadius={10}
                          fill={area.fill}
                        />
                        <Tooltip
                          formatter={(value) => `${value}%`}
                          contentStyle={{
                            backgroundColor: darkMode ? '#0f172a' : '#ffffff',
                            borderColor: darkMode ? '#1f2937' : '#e5e7eb',
                            color: darkMode ? '#e2e8f0' : '#1f2937',
                          }}
                        />
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedArea && (
            <div
              className={`rounded-2xl border p-5 ${
                darkMode ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h4 className="text-lg font-semibold">{selectedArea.label}</h4>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Precisión {selectedArea.accuracy}% · {selectedArea.total} preguntas
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedAreaKey(null)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      darkMode
                        ? 'bg-slate-800 border border-slate-700 hover:border-slate-500 text-slate-200'
                        : 'bg-slate-100 border border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    Limpiar área
                  </button>
                </div>
              </div>

              {topicPerformanceData.length ? (
                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                  <div
                    className={`rounded-2xl border p-4 ${
                      darkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide">Comparativa por tema</h5>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={topicPerformanceData}
                        layout="vertical"
                        margin={{ top: 8, right: 20, bottom: 8, left: 80 }}
                        onClick={(chartData) => {
                          const topicKey = chartData?.activePayload?.[0]?.payload?.key;
                          if (topicKey) handleTopicFocus(topicKey);
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e5e7eb'} />
                        <XAxis type="number" allowDecimals={false} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
                        <YAxis type="category" dataKey="label" width={140} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: darkMode ? '#0f172a' : '#ffffff',
                            borderColor: darkMode ? '#1f2937' : '#e5e7eb',
                            color: darkMode ? '#e2e8f0' : '#1f2937',
                          }}
                        />
                        <Legend />
                        <Bar dataKey="correct" stackId="stack" name="Correctas" fill="#22c55e">
                          {topicPerformanceData.map((entry) => (
                            <Cell
                              key={`correct-${entry.key}`}
                              fillOpacity={selectedTopicKey && selectedTopicKey !== entry.key ? 0.4 : 1}
                            />
                          ))}
                        </Bar>
                        <Bar dataKey="incorrect" stackId="stack" name="Incorrectas" fill="#ef4444">
                          {topicPerformanceData.map((entry) => (
                            <Cell
                              key={`incorrect-${entry.key}`}
                              fillOpacity={selectedTopicKey && selectedTopicKey !== entry.key ? 0.4 : 1}
                            />
                          ))}
                        </Bar>
                        <Bar dataKey="skipped" stackId="stack" name="Sin responder" fill="#a855f7">
                          {topicPerformanceData.map((entry) => (
                            <Cell
                              key={`skipped-${entry.key}`}
                              fillOpacity={selectedTopicKey && selectedTopicKey !== entry.key ? 0.4 : 1}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <p className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Haz clic en un tema para ver sus subtemas y filtrar las preguntas.
                    </p>
                  </div>

                  <div
                    className={`rounded-2xl border p-4 ${
                      darkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide">Detalle por tema</h5>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead className={darkMode ? 'bg-slate-900/80 text-slate-300' : 'bg-slate-100 text-slate-600'}>
                          <tr>
                            <th className="px-3 py-2 text-left">Tema</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2 text-right">Correctas</th>
                            <th className="px-3 py-2 text-right">Incorrectas</th>
                            <th className="px-3 py-2 text-right">Sin resp.</th>
                            <th className="px-3 py-2 text-right">Precisión</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topicPerformanceData.map((topic) => {
                            const isActive = selectedTopicKey === topic.key;
                            return (
                              <tr
                                key={topic.key}
                                onClick={() => handleTopicFocus(topic.key)}
                                className={`cursor-pointer transition ${
                                  isActive
                                    ? darkMode
                                      ? 'bg-cyan-500/10 text-cyan-100'
                                      : 'bg-sky-100 text-sky-700'
                                    : darkMode
                                    ? 'odd:bg-slate-900/50'
                                    : 'odd:bg-slate-50'
                                }`}
                              >
                                <td className="px-3 py-2">{topic.label}</td>
                                <td className="px-3 py-2 text-right">{topic.total}</td>
                                <td className="px-3 py-2 text-right text-emerald-500">{topic.correct}</td>
                                <td className="px-3 py-2 text-right text-rose-500">{topic.incorrect}</td>
                                <td className="px-3 py-2 text-right text-indigo-500">{topic.skipped}</td>
                                <td className="px-3 py-2 text-right">
                                  <span
                                    className={
                                      topic.accuracy >= 75
                                        ? darkMode ? 'text-emerald-300' : 'text-emerald-600'
                                        : topic.accuracy >= 50
                                        ? darkMode ? 'text-amber-300' : 'text-amber-500'
                                        : darkMode ? 'text-rose-300' : 'text-rose-500'
                                    }
                                  >
                                    {topic.accuracy}%
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <p className={`mt-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  No hay información suficiente para esta área en el simulacro.
                </p>
              )}

              <div
                className={`mt-6 rounded-2xl border p-4 ${
                  darkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-white'
                }`}
              >
                {selectedTopicData ? (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h5 className="text-sm font-semibold uppercase tracking-wide">
                          Subtemas en {selectedTopicData.label}
                        </h5>
                        <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {(selectedTopicData.correct ?? 0)} aciertos · {(selectedTopicData.incorrect ?? 0)} errores ·{' '}
                          {(selectedTopicData.total ?? 0)} preguntas
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedTopicKey(null)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                          darkMode
                            ? 'bg-slate-800 border border-slate-700 hover:border-slate-500 text-slate-200'
                            : 'bg-slate-100 border border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        Limpiar tema
                      </button>
                    </div>
                    {selectedTopicSubtopics.length ? (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                          <thead className={darkMode ? 'bg-slate-900/80 text-slate-300' : 'bg-slate-100 text-slate-600'}>
                            <tr>
                              <th className="px-3 py-2 text-left">Subtema</th>
                              <th className="px-3 py-2 text-right">Total</th>
                              <th className="px-3 py-2 text-right">Correctas</th>
                              <th className="px-3 py-2 text-right">Incorrectas</th>
                              <th className="px-3 py-2 text-right">Sin resp.</th>
                              <th className="px-3 py-2 text-right">Precisión</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTopicSubtopics.map((subtopic) => (
                              <tr
                                key={subtopic.key}
                                className={darkMode ? 'odd:bg-slate-900/40 text-slate-300' : 'odd:bg-white text-slate-700'}
                              >
                                <td className="px-3 py-2">{subtopic.label}</td>
                                <td className="px-3 py-2 text-right">{subtopic.total}</td>
                                <td className="px-3 py-2 text-right text-emerald-500">{subtopic.correct}</td>
                                <td className="px-3 py-2 text-right text-rose-500">{subtopic.incorrect}</td>
                                <td className="px-3 py-2 text-right text-indigo-500">{subtopic.skipped}</td>
                                <td className="px-3 py-2 text-right">
                                  <span
                                    className={
                                      subtopic.accuracy >= 75
                                        ? darkMode ? 'text-emerald-300' : 'text-emerald-600'
                                        : subtopic.accuracy >= 50
                                        ? darkMode ? 'text-amber-300' : 'text-amber-500'
                                        : darkMode ? 'text-rose-300' : 'text-rose-500'
                                    }
                                  >
                                    {subtopic.accuracy}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className={`mt-3 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Aún no hay subtemas detallados para este tema.
                      </p>
                    )}
                  </>
                ) : (
                  <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Selecciona un tema en la tabla o en la gráfica para revisar sus subtemas y preguntas asociadas.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
          <div
            className={`rounded-2xl border p-4 ${
              darkMode ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                  <PieChartIcon className="w-4 h-4 text-emerald-400" />
                  Distribución de desempeño
                </h4>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {formatNumber(distributionTotals.total)} preguntas · {formatNumber(distributionTotals.correct)} correctas ·{' '}
                  {formatNumber(distributionTotals.incorrect)} incorrectas · {formatNumber(distributionTotals.skipped)} sin responder
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowDistributionExpanded(true)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                    darkMode
                      ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20'
                      : 'border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100'
                  }`}
                >
                  Ver en grande
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <div
                className={`rounded-xl border px-3 py-2 text-xs ${
                  darkMode ? 'border-slate-800 bg-slate-900/70 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                <p className="uppercase tracking-wide opacity-70">Precisión</p>
                <p className="mt-1 text-lg font-semibold text-emerald-500">{distributionTotals.accuracy}%</p>
              </div>
              <div
                className={`rounded-xl border px-3 py-2 text-xs ${
                  darkMode ? 'border-slate-800 bg-slate-900/70 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                <p className="uppercase tracking-wide opacity-70">Incorrectas</p>
                <p className="mt-1 text-lg font-semibold text-rose-400">
                  {formatNumber(distributionTotals.incorrect)} ({distributionTotals.incorrectPercentage}%)
                </p>
              </div>
              <div
                className={`rounded-xl border px-3 py-2 text-xs ${
                  darkMode ? 'border-slate-800 bg-slate-900/70 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                <p className="uppercase tracking-wide opacity-70">Sin responder</p>
                <p className="mt-1 text-lg font-semibold text-sky-400">
                  {formatNumber(distributionTotals.skipped)} ({distributionTotals.skippedPercentage}%)
                </p>
              </div>
              <div
                className={`rounded-xl border px-3 py-2 text-xs ${
                  darkMode ? 'border-slate-800 bg-slate-900/70 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                <p className="uppercase tracking-wide opacity-70">Correctas</p>
                <p className="mt-1 text-lg font-semibold text-emerald-400">{formatNumber(distributionTotals.correct)}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {distributionViewOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setDistributionViewMode(option.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    distributionViewMode === option.id
                      ? darkMode
                        ? 'bg-cyan-500 text-black'
                        : 'bg-sky-600 text-white'
                      : darkMode
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                {renderDistributionChart()}
              </ResponsiveContainer>
            </div>
          </div>
          </div>
        </div>

        <div
          className={`rounded-3xl border p-6 ${
            darkMode ? 'bg-slate-900/70 border-slate-800 text-slate-100' : 'bg-white border-slate-200 text-slate-700'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-xl font-semibold">Desempeño global por dimensión</h3>
            <div className="flex flex-wrap gap-2">
              {dimensionOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setResultsViewDimension(option.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    resultsViewDimension === option.id
                      ? darkMode
                        ? 'bg-cyan-500 text-black'
                        : 'bg-sky-600 text-white'
                      : darkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {breakdownRows.length === 0 ? (
            <p className={`mt-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              No hay datos suficientes para esta dimensión.
            </p>
          ) : (
            <div className={`mt-4 overflow-x-auto rounded-xl border ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
              <table className="w-full border-collapse text-sm">
                <thead className={darkMode ? 'bg-slate-900/80 text-slate-300' : 'bg-slate-100 text-slate-600'}>
                  <tr>
                    <th className="px-3 py-2 text-left uppercase tracking-wide">Categoría</th>
                    <th className="px-3 py-2 text-left uppercase tracking-wide">Total</th>
                    <th className="px-3 py-2 text-left uppercase tracking-wide">Correctas</th>
                    <th className="px-3 py-2 text-left uppercase tracking-wide">Incorrectas</th>
                    <th className="px-3 py-2 text-left uppercase tracking-wide">Precisión</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownRows.map((row) => (
                    <tr
                      key={row.key}
                      className={darkMode ? 'odd:bg-slate-900/50 text-slate-200' : 'odd:bg-slate-50 text-slate-700'}
                    >
                      <td className="px-3 py-2">{row.label}</td>
                      <td className="px-3 py-2">{row.total}</td>
                      <td className="px-3 py-2 text-emerald-500">{row.correct}</td>
                      <td className="px-3 py-2 text-rose-500">{row.errors}</td>
                      <td className="px-3 py-2 text-sky-500">{row.accuracy}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 text-sm">
            <p className={`mb-2 flex items-center gap-2 font-semibold ${darkMode ? 'text-cyan-300' : 'text-slate-800'}`}>
              <ListChecks className="w-4 h-4" />
              Sugerencias de estudio
            </p>
            <ul className={`list-disc list-inside ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              {coachingInsights.map((tip, idx) => (
                <li key={`coaching-${idx}`}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className={`rounded-3xl border p-6 ${
            darkMode ? 'bg-slate-900/70 border-slate-800 text-slate-100' : 'bg-white border-slate-200 text-slate-700'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-semibold">Revisión de preguntas</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Área: {selectedAreaLabel} · Tema: {selectedTopicLabel}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                Tiempo promedio: {averageTimePerQuestion}
                {allocatedSeconds ? ` · Tiempo asignado: ${formatDuration(allocatedSeconds)}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {QUESTION_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setQuestionReviewFilter(filter.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    questionReviewFilter === filter.id
                      ? darkMode
                        ? 'bg-cyan-500 text-black'
                        : 'bg-sky-600 text-white'
                      : darkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Mostrando {filteredReviewQuestions.length} de {questionSummaries.length} preguntas.
          </p>

          {selectedArea && topicHeatmap.length > 0 && (
            <div
              className={`mt-4 rounded-2xl border p-4 ${
                darkMode ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold uppercase tracking-wide">
                  Termómetro de subtemas en {selectedArea.label}
                </h4>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Verde = alto desempeño · Rojo = reforzar
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {topicHeatmap.map((topic) => {
                  const accuracy = topic.accuracy ?? 0;
                  const heatColor =
                    accuracy >= 75
                      ? darkMode
                        ? 'from-emerald-500/20 via-emerald-500/10 to-transparent text-emerald-200 border-emerald-500/40'
                        : 'from-emerald-100 via-emerald-50 to-transparent text-emerald-700 border-emerald-200'
                      : accuracy >= 50
                      ? darkMode
                        ? 'from-amber-500/20 via-amber-500/10 to-transparent text-amber-200 border-amber-500/40'
                        : 'from-amber-100 via-amber-50 to-transparent text-amber-700 border-amber-200'
                      : darkMode
                      ? 'from-rose-500/20 via-rose-500/10 to-transparent text-rose-200 border-rose-500/40'
                      : 'from-rose-100 via-rose-50 to-transparent text-rose-700 border-rose-200';
                  return (
                    <div
                      key={topic.topic}
                      className={`rounded-2xl border px-4 py-3 bg-gradient-to-br ${heatColor}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{topic.topic}</span>
                        <span className="text-xs font-semibold">{accuracy}%</span>
                      </div>
                      <p className={`mt-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {topic.incorrect} errores de {topic.total} preguntas
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-4 max-h-96 overflow-y-auto space-y-3">
            {filteredReviewQuestions.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                No hay preguntas que coincidan con el filtro seleccionado.
              </p>
            ) : (
              filteredReviewQuestions.map((item) => {
                const q = item.question || {};
                const options = dedupeOptions(Array.isArray(q.options) ? q.options : []);
                const statusBadgeClass =
                  item.status === 'correct'
                    ? darkMode
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
                      : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : item.status === 'incorrect'
                    ? darkMode
                      ? 'bg-rose-500/20 text-rose-300 border border-rose-400/40'
                      : 'bg-rose-100 text-rose-600 border border-rose-200'
                    : darkMode
                    ? 'bg-slate-800 text-slate-300 border border-slate-700'
                    : 'bg-slate-100 text-slate-600 border border-slate-300';

                return (
                  <div
                    key={`review-${item.index}-${q.id || item.index}`}
                    className={`rounded-2xl border p-4 ${
                      darkMode ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass}`}>
                          {item.status === 'correct'
                            ? 'Correcta'
                            : item.status === 'incorrect'
                            ? 'Incorrecta'
                            : 'Sin responder'}
                        </span>
                        {item.flagged && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold border border-amber-400 text-amber-500 bg-amber-400/10">
                            Marcada
                          </span>
                        )}
                      </div>
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Pregunta {item.index + 1} · {q.year || 'Sin año'}
                      </span>
                    </div>

                    {q.case_description && (
                      <p className={`mt-3 italic ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{q.case_description}</p>
                    )}

                    <p className={`mt-3 font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      {q.question_text}
                    </p>

                    <div className="mt-3 space-y-2">
                      {options.map((opt) => {
                        const isUserAnswer =
                          (opt.id !== undefined && opt.id === item.userAnswerId) ||
                          (!opt.id && item.userAnswerLabel && opt.option_label === item.userAnswerLabel);
                        const isCorrectOption = !!opt.correct;
                        const optionClass = isUserAnswer
                          ? isCorrectOption
                            ? darkMode
                              ? 'bg-emerald-900/40 border-l-4 border-emerald-400'
                              : 'bg-emerald-50 border-l-4 border-emerald-300'
                            : darkMode
                            ? 'bg-rose-900/40 border-l-4 border-rose-400'
                            : 'bg-rose-50 border-l-4 border-rose-300'
                          : isCorrectOption
                          ? darkMode
                            ? 'bg-emerald-900/20 border border-emerald-500/30'
                            : 'bg-emerald-50 border border-emerald-200'
                          : darkMode
                          ? 'bg-slate-900/60 border border-transparent'
                          : 'bg-slate-100 border border-transparent';

                        return (
                          <div key={`${q.id || item.index}-${opt.option_label}`} className={`px-3 py-2 rounded-xl ${optionClass}`}>
                            <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>
                              {opt.option_label}) {opt.option_text}
                            </span>
                            {isCorrectOption && (
                              <span className={`ml-2 text-xs font-semibold ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                                (Correcta)
                              </span>
                            )}
                            {isUserAnswer && !isCorrectOption && (
                              <span className={`ml-2 text-xs font-semibold ${darkMode ? 'text-rose-300' : 'text-rose-600'}`}>
                                (Tu respuesta)
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {q.explanation && (
                      <div className="mt-3">
                        <h5 className={`text-sm font-semibold ${darkMode ? 'text-cyan-300' : 'text-slate-800'}`}>Explicación</h5>
                        <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{q.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {(onReset || onBack) && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
            {typeof onReset === 'function' && (
              <button
                type="button"
                onClick={onReset}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  darkMode
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.3)]'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                Reiniciar
              </button>
            )}
            {typeof onBack === 'function' && (
              <button
                type="button"
                onClick={onBack}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  darkMode
                    ? 'bg-slate-800 border border-slate-700 hover:border-slate-500 text-slate-200'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
              >
                {fromGenerator ? 'Regresar' : 'Volver'}
              </button>
            )}
          </div>
        )}
      </div>
      {showDistributionExpanded && (
        <DistributionDrilldownOverlay
          darkMode={darkMode}
          onClose={() => setShowDistributionExpanded(false)}
          formatNumber={formatNumber}
          distributionTotals={distributionTotals}
          stackedAreaData={stackedAreaData}
          distributionViewMode={distributionViewMode}
          setDistributionViewMode={setDistributionViewMode}
          distributionViewOptions={distributionViewOptions}
          renderChart={renderDistributionChart}
        />
      )}
    </>
  );
}

export default SimulationResultsPanel;

function DistributionDrilldownOverlay({
  darkMode,
  onClose,
  formatNumber,
  distributionTotals,
  stackedAreaData,
  distributionViewMode,
  setDistributionViewMode,
  distributionViewOptions,
  renderChart,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const sortedByAccuracy = useMemo(
    () =>
      [...stackedAreaData]
        .map((entry) => {
          const total = Math.max(entry.correct + entry.incorrect + entry.skipped, 1);
          return {
            ...entry,
            total,
            accuracyPct: Math.round((entry.correct / total) * 100),
            incorrectPct: Math.round((entry.incorrect / total) * 100),
          };
        })
        .sort((a, b) => a.accuracyPct - b.accuracyPct),
    [stackedAreaData]
  );

  const topChallengingAreas = sortedByAccuracy.slice(0, 5);

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 z-[130] flex flex-col bg-slate-900/85 backdrop-blur-xl ${
        darkMode ? 'text-slate-100' : 'text-slate-800'
      }`}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-6 overflow-hidden px-4 py-8 sm:px-8">
        <div
          className={`flex flex-wrap items-center justify-between gap-4 rounded-3xl border px-6 py-4 shadow-2xl ${
            darkMode ? 'border-slate-700 bg-slate-900/95' : 'border-slate-200 bg-white'
          }`}
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] opacity-70">Análisis global</p>
            <h2 className="mt-1 text-2xl font-bold">Distribución de correctas vs incorrectas</h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {formatNumber(distributionTotals.total)} preguntas · {formatNumber(distributionTotals.correct)} correctas ·{' '}
              {formatNumber(distributionTotals.incorrect)} incorrectas · {formatNumber(distributionTotals.skipped)} sin responder
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
              darkMode
                ? 'border-slate-700 hover:border-slate-500 text-slate-200'
                : 'border-slate-200 hover:border-slate-300 text-slate-600'
            }`}
          >
            Cerrar
          </button>
        </div>

        <div
          className={`flex-1 overflow-hidden rounded-3xl border ${
            darkMode ? 'border-slate-800 bg-slate-950/70' : 'border-slate-200 bg-white'
          }`}
        >
          <div className="flex h-full flex-col gap-5 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                <div
                  className={`rounded-xl border px-3 py-2 ${
                    darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  <p className="uppercase tracking-wide opacity-70">Precisión global</p>
                  <p className="mt-1 text-xl font-semibold text-emerald-400">{distributionTotals.accuracy}%</p>
                </div>
                <div
                  className={`rounded-xl border px-3 py-2 ${
                    darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  <p className="uppercase tracking-wide opacity-70">Incorrectas</p>
                  <p className="mt-1 text-xl font-semibold text-rose-400">{formatNumber(distributionTotals.incorrect)}</p>
                </div>
                <div
                  className={`rounded-xl border px-3 py-2 ${
                    darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  <p className="uppercase tracking-wide opacity-70">Sin responder</p>
                  <p className="mt-1 text-xl font-semibold text-sky-400">{formatNumber(distributionTotals.skipped)}</p>
                </div>
                <div
                  className={`rounded-xl border px-3 py-2 ${
                    darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  <p className="uppercase tracking-wide opacity-70">Correctas</p>
                  <p className="mt-1 text-xl font-semibold text-emerald-300">{formatNumber(distributionTotals.correct)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {distributionViewOptions.map((option) => (
                  <button
                    key={`distribution-overlay-${option.id}`}
                    type="button"
                    onClick={() => setDistributionViewMode(option.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                      distributionViewMode === option.id
                        ? darkMode
                          ? 'bg-cyan-500 text-black'
                          : 'bg-sky-600 text-white'
                        : darkMode
                        ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-[420px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                {renderChart()}
              </ResponsiveContainer>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide">Áreas con menor precisión</h3>
              {topChallengingAreas.length === 0 ? (
                <p className={`mt-2 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  No se detectaron áreas problemáticas en esta evaluación.
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-xl border border-transparent">
                  <table className="w-full border-collapse text-sm">
                    <thead className={darkMode ? 'bg-slate-900/80 text-slate-300' : 'bg-slate-100 text-slate-600'}>
                      <tr>
                        <th className="px-3 py-2 text-left">Área</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-right">Correctas</th>
                        <th className="px-3 py-2 text-right">Incorrectas</th>
                        <th className="px-3 py-2 text-right">Precisión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topChallengingAreas.map((area) => (
                        <tr key={`distribution-area-${area.area}`} className={darkMode ? 'odd:bg-slate-900/40' : 'odd:bg-slate-50'}>
                          <td className="px-3 py-2 text-sm font-medium">{area.area}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(area.total)}</td>
                          <td className="px-3 py-2 text-right text-emerald-500">{formatNumber(area.correct)}</td>
                          <td className="px-3 py-2 text-right text-rose-500">{formatNumber(area.incorrect)}</td>
                          <td className="px-3 py-2 text-right">
                            <span
                              className={
                                area.accuracyPct >= 75
                                  ? darkMode ? 'text-emerald-300' : 'text-emerald-600'
                                  : area.accuracyPct >= 50
                                  ? darkMode ? 'text-amber-300' : 'text-amber-500'
                                  : darkMode ? 'text-rose-300' : 'text-rose-500'
                              }
                            >
                              {area.accuracyPct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AreaDrilldownView({ area, darkMode, onBack, formatNumber, formatPercent, palette, questionSummaries }) {
  const containerRef = useRef(null);
  const [activeTopicKey, setActiveTopicKey] = useState(null);
  const [localFilter, setLocalFilter] = useState('all');
  const [activeSubtopicKey, setActiveSubtopicKey] = useState(null);
  const [activeSubtopicLabel, setActiveSubtopicLabel] = useState(null);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [area.label]);

  useEffect(() => {
    setLocalFilter('all');
    setActiveSubtopicKey(null);
    setActiveSubtopicLabel(null);
    setShowAllTopics(false);
  }, [area.label]);

  const areaQuestions = useMemo(
    () => (questionSummaries || []).filter((item) => item.area === area.label),
    [questionSummaries, area.label]
  );

  const topicPerformance = useMemo(() => {
    const hasTopicTree = Array.isArray(area.topics) && area.topics.length > 0;
    const computeAccuracy = (correct, total) => (total > 0 ? Math.round((correct / total) * 100) : 0);

    if (hasTopicTree) {
      return area.topics.map((topic, index) => {
        const skipped =
          typeof topic.skipped === 'number'
            ? topic.skipped
            : Math.max((topic.total || 0) - (topic.correct || 0) - (topic.incorrect || 0), 0);
        return {
          key: topic.key,
          label: topic.label,
          total: topic.total,
          correct: topic.correct,
          incorrect: topic.incorrect,
          skipped,
          accuracy: Math.round(topic.accuracy || 0),
          fill: palette[index % palette.length],
          base: topic,
        };
      });
    }

    const topicMap = new Map();

    areaQuestions.forEach((item) => {
      const topicLabel = item.topic || 'Tema sin título';
      const topicKey = topicLabel;
      const subtopicLabel = item.subtopic || 'Sin subtema';
      const status = item.status;

      if (!topicMap.has(topicKey)) {
        topicMap.set(topicKey, {
          key: topicKey,
          label: topicLabel,
          total: 0,
          correct: 0,
          incorrect: 0,
          skipped: 0,
          subtopics: new Map(),
        });
      }

      const topicEntry = topicMap.get(topicKey);
      topicEntry.total += 1;
      if (status === 'correct') topicEntry.correct += 1;
      else if (status === 'incorrect') topicEntry.incorrect += 1;
      else topicEntry.skipped += 1;

      if (!topicEntry.subtopics.has(subtopicLabel)) {
        topicEntry.subtopics.set(subtopicLabel, {
          key: `${topicKey}::${subtopicLabel}`,
          label: subtopicLabel,
          total: 0,
          correct: 0,
          incorrect: 0,
          skipped: 0,
        });
      }

      const subtopicEntry = topicEntry.subtopics.get(subtopicLabel);
      subtopicEntry.total += 1;
      if (status === 'correct') subtopicEntry.correct += 1;
      else if (status === 'incorrect') subtopicEntry.incorrect += 1;
      else subtopicEntry.skipped += 1;
    });

    return Array.from(topicMap.values())
      .sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total;
        return a.label.localeCompare(b.label, 'es');
      })
      .map((topic, index) => {
        const fallbackSubtopics = Array.from(topic.subtopics.values())
          .sort((a, b) => {
            if (b.total !== a.total) return b.total - a.total;
            return a.label.localeCompare(b.label, 'es');
          })
          .map((subtopic) => ({
            key: subtopic.key,
            label: subtopic.label,
            total: subtopic.total,
            correct: subtopic.correct,
            incorrect: subtopic.incorrect,
            skipped: subtopic.skipped,
            accuracy: computeAccuracy(subtopic.correct, subtopic.total),
          }));

        return {
          key: topic.key,
          label: topic.label,
          total: topic.total,
          correct: topic.correct,
          incorrect: topic.incorrect,
          skipped: topic.skipped,
          accuracy: computeAccuracy(topic.correct, topic.total),
          fill: palette[index % palette.length],
          base: {
            subtopics: fallbackSubtopics,
          },
        };
      });
  }, [area, areaQuestions, palette]);

  useEffect(() => {
    if (!topicPerformance.length) {
      setActiveTopicKey(null);
      return;
    }
    if (showAllTopics) {
      setActiveTopicKey(null);
      return;
    }
    setActiveTopicKey((prev) => {
      if (prev && topicPerformance.some((topic) => topic.key === prev)) {
        return prev;
      }
      return topicPerformance[0].key;
    });
  }, [topicPerformance, showAllTopics]);

  useEffect(() => {
    setActiveSubtopicKey(null);
    setActiveSubtopicLabel(null);
  }, [activeTopicKey, showAllTopics]);

  const activeTopic = useMemo(() => {
    if (!topicPerformance.length) return null;
    if (showAllTopics || !activeTopicKey) return null;
    const match = topicPerformance.find((topic) => topic.key === activeTopicKey);
    return match || null;
  }, [topicPerformance, activeTopicKey, showAllTopics]);
  const activeTopicLabel = activeTopic?.label || null;
  const topicDisplayLabel = showAllTopics ? 'Todos los temas' : activeTopicLabel || 'Todos los temas';
  const subtopicDisplayLabel = activeSubtopicLabel || 'Todos los subtemas';

  const activeSubtopics = useMemo(() => {
    if (!activeTopic?.base?.subtopics) return [];
    return activeTopic.base.subtopics.map((subtopic) => {
      const displayLabel = subtopic.label || 'Sin subtema';
      return {
        ...subtopic,
        displayLabel,
        accuracy: Math.round(subtopic.accuracy || 0),
        skipped:
          typeof subtopic.skipped === 'number'
            ? subtopic.skipped
            : Math.max((subtopic.total || 0) - (subtopic.correct || 0) - (subtopic.incorrect || 0), 0),
      };
    });
  }, [activeTopic]);

  const topicScopedQuestions = useMemo(() => {
    return areaQuestions.filter((item) => {
      if (!showAllTopics && activeTopicLabel && item.topic !== activeTopicLabel) {
        return false;
      }
      if (activeSubtopicLabel) {
        const itemSubtopicLabel = item.subtopic || 'Sin subtema';
        if (itemSubtopicLabel !== activeSubtopicLabel) {
          return false;
        }
      }
      return true;
    });
  }, [areaQuestions, activeTopicLabel, activeSubtopicLabel, showAllTopics]);

  const filteredQuestions = useMemo(
    () => topicScopedQuestions.filter((item) => matchesFilter(item.status, localFilter, item.flagged)),
    [topicScopedQuestions, localFilter]
  );

  useEffect(() => {
    setExpandedQuestionIndex(null);
  }, [localFilter, topicScopedQuestions.length, activeTopicKey, activeSubtopicKey, showAllTopics]);

  const exportIncorrectQuestions = useCallback(
    (includeDetails) => {
      if (localFilter !== 'incorrect' || filteredQuestions.length === 0) return;
      try {
        setIsDownloading(true);
        const lines = [];
        filteredQuestions.forEach((item, idx) => {
          const q = item.question || {};
          const header = `Pregunta ${idx + 1}`;
          lines.push(header);
          lines.push('-'.repeat(header.length));
          if (q.case_description) {
            lines.push(`Caso: ${q.case_description}`);
          }
          lines.push(`Enunciado: ${q.question_text || 'Sin enunciado disponible'}`);
          const options = dedupeOptions(Array.isArray(q.options) ? q.options : []);
          options.forEach((opt) => {
            const label = opt.option_label || '';
            const text = opt.option_text || '';
            if (includeDetails) {
              const markers = [
                opt.correct ? '(Correcta)' : '',
                item.userAnswerId && opt.id === item.userAnswerId ? '(Tu respuesta)' : '',
              ]
                .filter(Boolean)
                .join(' ');
              lines.push(`  ${label}) ${text} ${markers}`.trim());
            } else {
              lines.push(`  ${label}) ${text}`.trim());
            }
          });
          if (includeDetails && q.explanation) {
            lines.push(`Explicación: ${q.explanation}`);
          }
          lines.push('');
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        const detailSuffix = includeDetails ? 'con-detalle' : 'sin-detalle';
        link.href = url;
        link.download = `mediexampro_${toSafeFilename(area.label)}_${detailSuffix}_${timestamp}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exportando preguntas incorrectas:', error);
      } finally {
        setIsDownloading(false);
      }
    },
    [area.label, filteredQuestions, localFilter]
  );

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Exportar a TXT (con o sin claves)
  const exportToTXT = useCallback(
    (withKeys) => {
      const questionsToExport = areaQuestions;
      if (questionsToExport.length === 0) return;

      try {
        setIsDownloading(true);
        const lines = [];
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

        lines.push(`ÁREA: ${area.label}`);
        lines.push(`Fecha de exportación: ${new Date().toLocaleDateString('es-PE')}`);
        lines.push(`Total de preguntas: ${questionsToExport.length}`);
        lines.push('='.repeat(60));
        lines.push('');

        questionsToExport.forEach((item, idx) => {
          const q = item.question || {};
          lines.push(`PREGUNTA ${idx + 1}`);
          lines.push('-'.repeat(40));

          if (q.case_description) {
            lines.push(`CASO CLÍNICO:`);
            lines.push(q.case_description);
            lines.push('');
          }

          lines.push(`ENUNCIADO:`);
          lines.push(q.question_text || 'Sin enunciado disponible');
          lines.push('');

          const options = dedupeOptions(Array.isArray(q.options) ? q.options : []);
          lines.push('OPCIONES:');
          let correctAnswer = '';
          options.forEach((opt) => {
            const label = opt.option_label || '';
            const text = opt.option_text || '';
            lines.push(`  ${label}) ${text}`);
            if (opt.correct) {
              correctAnswer = `${label}) ${text}`;
            }
          });

          if (withKeys && correctAnswer) {
            lines.push('');
            lines.push(`CLAVE: ${correctAnswer}`);
          }

          lines.push('');
          lines.push('');
        });

        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const keysSuffix = withKeys ? 'CON-CLAVES' : 'SIN-CLAVES';
        link.href = url;
        link.download = `${toSafeFilename(area.label)}_${keysSuffix}_${timestamp}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exportando a TXT:', error);
      } finally {
        setIsDownloading(false);
        setShowExportMenu(false);
      }
    },
    [area.label, areaQuestions]
  );

  // Exportar a PDF (con o sin claves)
  const exportToPDF = useCallback(
    (withKeys) => {
      const questionsToExport = areaQuestions;
      if (questionsToExport.length === 0) return;

      try {
        setIsDownloading(true);
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const maxWidth = pageWidth - 2 * margin;
        let yPos = margin;

        const addNewPageIfNeeded = (requiredSpace = 20) => {
          if (yPos + requiredSpace > pageHeight - margin) {
            doc.addPage();
            yPos = margin;
            return true;
          }
          return false;
        };

        // Título
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`ÁREA: ${area.label}`, margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Fecha: ${new Date().toLocaleDateString('es-PE')} | Total: ${questionsToExport.length} preguntas`, margin, yPos);
        yPos += 5;

        const keyLabel = withKeys ? 'CON CLAVES' : 'SIN CLAVES';
        doc.text(`Exportado: ${keyLabel}`, margin, yPos);
        yPos += 10;

        // Línea separadora
        doc.setDrawColor(200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;

        questionsToExport.forEach((item, idx) => {
          const q = item.question || {};

          addNewPageIfNeeded(40);

          // Número de pregunta
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text(`Pregunta ${idx + 1}`, margin, yPos);
          yPos += 6;

          // Caso clínico si existe
          if (q.case_description) {
            addNewPageIfNeeded(20);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bolditalic');
            doc.text('Caso clínico:', margin, yPos);
            yPos += 4;
            doc.setFont('helvetica', 'italic');
            const caseLines = doc.splitTextToSize(q.case_description, maxWidth);
            caseLines.forEach((line) => {
              addNewPageIfNeeded(5);
              doc.text(line, margin, yPos);
              yPos += 4;
            });
            yPos += 2;
          }

          // Enunciado
          addNewPageIfNeeded(15);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const questionLines = doc.splitTextToSize(q.question_text || 'Sin enunciado', maxWidth);
          questionLines.forEach((line) => {
            addNewPageIfNeeded(5);
            doc.text(line, margin, yPos);
            yPos += 5;
          });
          yPos += 3;

          // Opciones
          const options = dedupeOptions(Array.isArray(q.options) ? q.options : []);
          options.forEach((opt) => {
            addNewPageIfNeeded(6);
            const label = opt.option_label || '';
            const text = opt.option_text || '';
            const isCorrect = opt.correct;

            if (withKeys && isCorrect) {
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(0, 128, 0);
            } else {
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(0, 0, 0);
            }

            const optionText = `${label}) ${text}${withKeys && isCorrect ? ' ✓' : ''}`;
            const optLines = doc.splitTextToSize(optionText, maxWidth - 5);
            optLines.forEach((line) => {
              addNewPageIfNeeded(5);
              doc.text(line, margin + 3, yPos);
              yPos += 4.5;
            });
          });

          doc.setTextColor(0, 0, 0);

          // Explicación si withKeys
          if (withKeys && q.explanation) {
            yPos += 2;
            addNewPageIfNeeded(15);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bolditalic');
            doc.text('Explicación:', margin, yPos);
            yPos += 4;
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(80, 80, 80);
            const expLines = doc.splitTextToSize(q.explanation, maxWidth);
            expLines.forEach((line) => {
              addNewPageIfNeeded(4);
              doc.text(line, margin, yPos);
              yPos += 4;
            });
            doc.setTextColor(0, 0, 0);
          }

          yPos += 8;

          // Línea separadora entre preguntas
          if (idx < questionsToExport.length - 1) {
            addNewPageIfNeeded(5);
            doc.setDrawColor(220);
            doc.line(margin, yPos - 3, pageWidth - margin, yPos - 3);
          }
        });

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        const keysSuffix = withKeys ? 'CON-CLAVES' : 'SIN-CLAVES';
        doc.save(`${toSafeFilename(area.label)}_${keysSuffix}_${timestamp}.pdf`);
      } catch (error) {
        console.error('Error exportando a PDF:', error);
      } finally {
        setIsDownloading(false);
        setShowExportMenu(false);
      }
    },
    [area.label, areaQuestions]
  );

  return (
    <div
      ref={containerRef}
      className={`min-h-screen w-full ${darkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-800'}`}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-10 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={onBack}
              className={`mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
                darkMode
                  ? 'border-slate-700 hover:border-slate-500 text-slate-200'
                  : 'border-slate-200 hover:border-slate-300 text-slate-600'
              }`}
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al resumen
            </button>
            <h1 className="text-3xl font-bold leading-tight">{area.label}</h1>
            <p className={`mt-2 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {formatPercent(area.accuracy)} de precisión · {formatNumber(area.total)} preguntas analizadas
            </p>
          </div>
          <div className="flex flex-col gap-3 items-end">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <DetailBadge darkMode={darkMode} title="Precisión" value={formatPercent(area.accuracy)} accent="sky" />
              <DetailBadge darkMode={darkMode} title="Totales" value={formatNumber(area.total)} accent="slate" />
              <DetailBadge darkMode={darkMode} title="Correctas" value={formatNumber(area.correct)} accent="emerald" />
              <DetailBadge darkMode={darkMode} title="Incorrectas" value={formatNumber(area.incorrect)} accent="rose" />
            </div>

            {/* Botón de descarga con menú desplegable */}
            <div className="relative" ref={exportMenuRef}>
              <button
                type="button"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isDownloading || areaQuestions.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition ${
                  darkMode
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                    : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white'
                } ${isDownloading || areaQuestions.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Download className="h-4 w-4" />
                {isDownloading ? 'Descargando...' : 'Descargar Área'}
                <ChevronDown className={`h-4 w-4 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
              </button>

              {showExportMenu && (
                <div
                  className={`absolute right-0 top-full mt-2 w-56 rounded-xl border shadow-xl z-50 overflow-hidden ${
                    darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
                  }`}
                >
                  <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                    darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-50 text-slate-500'
                  }`}>
                    Formato PDF
                  </div>
                  <button
                    type="button"
                    onClick={() => exportToPDF(false)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                      darkMode ? 'hover:bg-slate-800 text-slate-200' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <FileDown className="h-4 w-4 text-rose-500" />
                    PDF sin claves
                  </button>
                  <button
                    type="button"
                    onClick={() => exportToPDF(true)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                      darkMode ? 'hover:bg-slate-800 text-slate-200' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <FileDown className="h-4 w-4 text-emerald-500" />
                    PDF con claves
                  </button>

                  <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                    darkMode ? 'bg-slate-800 text-slate-400 border-t border-slate-700' : 'bg-slate-50 text-slate-500 border-t border-slate-200'
                  }`}>
                    Formato TXT
                  </div>
                  <button
                    type="button"
                    onClick={() => exportToTXT(false)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                      darkMode ? 'hover:bg-slate-800 text-slate-200' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <FileText className="h-4 w-4 text-rose-500" />
                    TXT sin claves
                  </button>
                  <button
                    type="button"
                    onClick={() => exportToTXT(true)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                      darkMode ? 'hover:bg-slate-800 text-slate-200' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <FileText className="h-4 w-4 text-emerald-500" />
                    TXT con claves
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div
            className={`rounded-3xl border p-6 ${
              darkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold">Comparativa por tema</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAllTopics(true);
                    setActiveTopicKey(null);
                    setActiveSubtopicKey(null);
                    setActiveSubtopicLabel(null);
                  }}
                  className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${
                    showAllTopics
                      ? darkMode
                        ? 'border-cyan-400 text-cyan-200 bg-cyan-500/10'
                        : 'border-sky-400 text-sky-600 bg-sky-100'
                      : darkMode
                        ? 'border-slate-700 text-slate-300 hover:border-cyan-400/60'
                        : 'border-slate-200 text-slate-600 hover:border-sky-300'
                  }`}
                >
                  Mostrar todos los temas
                </button>
                {showAllTopics && (
                  <button
                    type="button"
                    onClick={() => setShowAllTopics(false)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${
                      darkMode
                        ? 'border-cyan-500 text-cyan-200 hover:border-cyan-400'
                        : 'border-sky-400 text-sky-600 hover:border-sky-500'
                    }`}
                  >
                    Volver a selección
                  </button>
                )}
              </div>
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Selecciona un tema para explorar sus subtemas
              </span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={topicPerformance}
                layout="vertical"
                margin={{ top: 0, right: 20, bottom: 0, left: 120 }}
                onClick={(chartData) => {
                  const topicKey = chartData?.activePayload?.[0]?.payload?.key;
                  if (topicKey) {
                    setShowAllTopics(false);
                    setActiveTopicKey(topicKey);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e2e8f0'} />
                <XAxis type="number" allowDecimals={false} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
                <YAxis dataKey="label" type="category" width={140} stroke={darkMode ? '#cbd5f5' : '#1f2937'} />
                <Tooltip
                  formatter={(value) => formatNumber(value)}
                  contentStyle={{
                    backgroundColor: darkMode ? '#0f172a' : '#ffffff',
                    borderColor: darkMode ? '#1f2937' : '#e2e8f0',
                    color: darkMode ? '#e2e8f0' : '#1f2937',
                  }}
                />
                <Legend />
                <Bar dataKey="correct" name="Correctas" stackId="stack" fill="#22c55e">
                  {topicPerformance.map((entry) => (
                    <Cell
                      key={`detail-correct-${entry.key}`}
                      fillOpacity={activeTopic?.key && activeTopic.key !== entry.key ? 0.35 : 1}
                    />
                  ))}
                </Bar>
                <Bar dataKey="incorrect" name="Incorrectas" stackId="stack" fill="#ef4444">
                  {topicPerformance.map((entry) => (
                    <Cell
                      key={`detail-incorrect-${entry.key}`}
                      fillOpacity={activeTopic?.key && activeTopic.key !== entry.key ? 0.35 : 1}
                    />
                  ))}
                </Bar>
                <Bar dataKey="skipped" name="Sin responder" stackId="stack" fill="#8b5cf6">
                  {topicPerformance.map((entry) => (
                    <Cell
                      key={`detail-skipped-${entry.key}`}
                      fillOpacity={activeTopic?.key && activeTopic.key !== entry.key ? 0.35 : 1}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div
            className={`rounded-3xl border p-6 ${
              darkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Detalle por tema</h2>
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Haz clic en una fila para enfocar los subtemas.
              </span>
            </div>
            {topicPerformance.length ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className={darkMode ? 'bg-slate-900/80 text-slate-300' : 'bg-slate-100 text-slate-600'}>
                    <tr>
                      <th className="px-3 py-2 text-left">Tema</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-right">Correctas</th>
                      <th className="px-3 py-2 text-right">Incorrectas</th>
                      <th className="px-3 py-2 text-right">Sin resp.</th>
                      <th className="px-3 py-2 text-right">Precisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topicPerformance.map((topic) => {
                      const isActive = activeTopic?.key === topic.key;
                      return (
                        <tr
                          key={topic.key}
                          onClick={() => setActiveTopicKey(topic.key)}
                          className={`cursor-pointer transition ${
                            isActive
                              ? darkMode
                                ? 'bg-cyan-500/10 text-cyan-100'
                                : 'bg-sky-100 text-sky-700'
                              : darkMode
                              ? 'odd:bg-slate-900/40 text-slate-300 hover:bg-slate-800/40'
                              : 'odd:bg-white text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <td className="px-3 py-2">{topic.label || 'Tema sin nombre'}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(topic.total)}</td>
                          <td className="px-3 py-2 text-right text-emerald-500">{formatNumber(topic.correct)}</td>
                          <td className="px-3 py-2 text-right text-rose-500">{formatNumber(topic.incorrect)}</td>
                          <td className="px-3 py-2 text-right text-indigo-400">{formatNumber(topic.skipped)}</td>
                          <td className="px-3 py-2 text-right">
                            <span
                              className={
                                topic.accuracy >= 75
                                  ? darkMode ? 'text-emerald-300' : 'text-emerald-600'
                                  : topic.accuracy >= 50
                                  ? darkMode ? 'text-amber-300' : 'text-amber-500'
                                  : darkMode ? 'text-rose-300' : 'text-rose-500'
                              }
                            >
                              {formatPercent(topic.accuracy)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Aún no hay información detallada por tema para esta área.
              </p>
            )}
          </div>
        </div>

        <div
          className={`rounded-3xl border p-6 ${
            darkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-white'
          }`}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">
              Subtemas · {activeTopic?.label || 'Selecciona un tema'}
            </h2>
            {activeTopic && (
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {formatNumber(activeTopic.correct)} correctas · {formatNumber(activeTopic.incorrect)} incorrectas
              </span>
            )}
          </div>
          {activeSubtopics.length ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead className={darkMode ? 'bg-slate-900/80 text-slate-300' : 'bg-slate-100 text-slate-600'}>
                  <tr>
                    <th className="px-3 py-2 text-left">Subtema</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Correctas</th>
                    <th className="px-3 py-2 text-right">Incorrectas</th>
                    <th className="px-3 py-2 text-right">Sin resp.</th>
                    <th className="px-3 py-2 text-right">Precisión</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSubtopics.map((subtopic) => {
                    const isActive = activeSubtopicKey === subtopic.key;
                    const inactiveClass = darkMode ? 'odd:bg-slate-900/40 text-slate-300' : 'odd:bg-white text-slate-700';
                    const activeClass = darkMode ? 'bg-cyan-500/10 text-cyan-200' : 'bg-sky-100 text-sky-700';
                    return (
                      <tr
                        key={subtopic.key}
                        onClick={() => {
                          if (isActive) {
                            setActiveSubtopicKey(null);
                            setActiveSubtopicLabel(null);
                          } else {
                            setActiveSubtopicKey(subtopic.key);
                            setActiveSubtopicLabel(subtopic.displayLabel);
                          }
                        }}
                        className={`cursor-pointer transition ${isActive ? activeClass : inactiveClass}`}
                      >
                        <td className="px-3 py-2">{subtopic.displayLabel}</td>
                        <td className="px-3 py-2 text-right">{formatNumber(subtopic.total)}</td>
                        <td className="px-3 py-2 text-right text-emerald-500">{formatNumber(subtopic.correct)}</td>
                        <td className="px-3 py-2 text-right text-rose-500">{formatNumber(subtopic.incorrect)}</td>
                        <td className="px-3 py-2 text-right text-indigo-400">{formatNumber(subtopic.skipped)}</td>
                        <td className="px-3 py-2 text-right">
                          <span
                            className={
                              subtopic.accuracy >= 75
                                ? darkMode ? 'text-emerald-300' : 'text-emerald-600'
                                : subtopic.accuracy >= 50
                                ? darkMode ? 'text-amber-300' : 'text-amber-500'
                                : darkMode ? 'text-rose-300' : 'text-rose-500'
                            }
                          >
                            {formatPercent(subtopic.accuracy)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Aún no hay subtemas detallados para este tema.
            </p>
          )}
        </div>

        <div
          className={`rounded-3xl border p-6 ${
            darkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-white'
          }`}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Preguntas asociadas</h2>
            <div className="flex flex-wrap gap-2 items-center">
              {QUESTION_FILTERS.map((filter) => (
                <button
                  key={`drilldown-filter-${filter.id}`}
                  type="button"
                  onClick={() => setLocalFilter(filter.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    localFilter === filter.id
                      ? darkMode
                        ? 'bg-cyan-500 text-black'
                        : 'bg-sky-600 text-white'
                      : darkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              {localFilter === 'incorrect' && filteredQuestions.length > 0 && (
                <div className="flex gap-2 ml-2">
                  <button
                    type="button"
                    onClick={() => exportIncorrectQuestions(false)}
                    disabled={isDownloading}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      darkMode
                        ? 'border-slate-700 text-slate-300 hover:border-slate-500'
                        : 'border-slate-200 text-slate-600 hover:border-slate-400'
                    } ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    TXT sin detalle
                  </button>
                  <button
                    type="button"
                    onClick={() => exportIncorrectQuestions(true)}
                    disabled={isDownloading}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      darkMode
                        ? 'border-cyan-500 text-cyan-200 hover:border-cyan-400'
                        : 'border-sky-400 text-sky-600 hover:border-sky-500'
                    } ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    TXT con detalle
                  </button>
                </div>
              )}
            </div>
          </div>
          <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Tema: {topicDisplayLabel} · Subtema: {subtopicDisplayLabel}
          </p>
          <p className={`mb-3 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Mostrando {filteredQuestions.length} de {topicScopedQuestions.length} preguntas.
          </p>

          <div className="space-y-3">
            {filteredQuestions.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                No se encontraron preguntas con el filtro seleccionado.
              </p>
            ) : (
              filteredQuestions.map((item) => {
                const q = item.question || {};
                const options = dedupeOptions(Array.isArray(q.options) ? q.options : []);
                const statusBadgeClass =
                  item.status === 'correct'
                    ? darkMode
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
                      : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : item.status === 'incorrect'
                    ? darkMode
                      ? 'bg-rose-500/20 text-rose-300 border border-rose-400/40'
                      : 'bg-rose-100 text-rose-600 border border-rose-200'
                    : darkMode
                    ? 'bg-slate-800 text-slate-300 border border-slate-700'
                    : 'bg-slate-100 text-slate-600 border border-slate-300';

                return (
                  <div
                    key={`drilldown-question-${item.index}`}
                    className={`rounded-2xl border p-4 ${
                      darkMode ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass}`}>
                          {item.status === 'correct'
                            ? 'Correcta'
                            : item.status === 'incorrect'
                            ? 'Incorrecta'
                            : 'Sin responder'}
                        </span>
                        <span className="rounded-full border px-2 py-1 text-xs opacity-75">
                          {item.topic || 'Tema sin título'}
                        </span>
                      </div>
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Pregunta {item.index + 1}
                      </span>
                    </div>
                    {q.case_description && (
                      <p className={`mt-3 italic ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {q.case_description}
                      </p>
                    )}
                    <div className="mt-3 flex items-start gap-3">
                      <p className={`font-semibold flex-1 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                        {q.question_text}
                      </p>
                      <TTSControls
                        text={`${q.case_description || ''} ${q.question_text || ''}`.trim()}
                        darkMode={darkMode}
                        questionId={`drilldown-${q.id ?? `${area.label}-${item.index}`}`}
                      />
                    </div>
                    <div className="mt-3 space-y-2">
                      {options.map((opt) => {
                        const isUserAnswer =
                          (opt.id !== undefined && opt.id === item.userAnswerId) ||
                          (!opt.id && item.userAnswerLabel && opt.option_label === item.userAnswerLabel);
                        const isCorrectOption = !!opt.correct;
                        const optionClass = isUserAnswer
                          ? isCorrectOption
                            ? darkMode
                              ? 'bg-emerald-900/40 border-l-4 border-emerald-400'
                              : 'bg-emerald-50 border-l-4 border-emerald-300'
                            : darkMode
                            ? 'bg-rose-900/40 border-l-4 border-rose-400'
                            : 'bg-rose-50 border-l-4 border-rose-300'
                          : isCorrectOption
                          ? darkMode
                            ? 'bg-emerald-900/20 border border-emerald-500/30'
                            : 'bg-emerald-50 border border-emerald-200'
                          : darkMode
                          ? 'bg-slate-900/60 border border-transparent'
                          : 'bg-slate-100 border border-transparent';

                        return (
                          <div key={`${item.index}-${opt.option_label}`} className={`rounded-xl px-3 py-2 ${optionClass}`}>
                            <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>
                              {opt.option_label}) {opt.option_text}
                            </span>
                            {isCorrectOption && (
                              <span className={`ml-2 text-xs font-semibold ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                                (Correcta)
                              </span>
                            )}
                            {isUserAnswer && !isCorrectOption && (
                              <span className={`ml-2 text-xs font-semibold ${darkMode ? 'text-rose-300' : 'text-rose-600'}`}>
                                (Tu respuesta)
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {(item.status === 'incorrect' || q.explanation) && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedQuestionIndex((prev) =>
                              prev === item.index ? null : item.index
                            )
                          }
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            darkMode
                              ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {expandedQuestionIndex === item.index ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              Ocultar explicación
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              Ver explicación
                            </>
                          )}
                        </button>
                        {expandedQuestionIndex === item.index && (
                          <div
                            className={`mt-3 rounded-2xl border p-4 ${
                              darkMode
                                ? 'border-slate-800 bg-slate-900/70'
                                : 'border-slate-200 bg-slate-50'
                            }`}
                          >
                            <h4
                              className={`flex items-center gap-2 text-sm font-semibold ${
                                darkMode ? 'text-slate-200' : 'text-slate-700'
                              }`}
                            >
                              <Info className="w-4 h-4" />
                              Detalle de la respuesta
                            </h4>
                            {item.status === 'incorrect' && (
                              <div className="mt-2 text-sm">
                                <p className={`font-semibold ${darkMode ? 'text-rose-300' : 'text-rose-600'}`}>
                                  Motivo de la respuesta seleccionada
                                </p>
                                {(() => {
                                  const userOption =
                                    options.find((opt) => opt.id === item.userAnswerId) ||
                                    options.find(
                                      (opt) =>
                                        (opt.option_label || '') === (item.userAnswerLabel || '')
                                    );
                                  const incorrectExplanation = userOption?.incorrect_explanation;
                                  if (!incorrectExplanation) {
                                    return (
                                      <p className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                                        Sin explicación específica para esta alternativa.
                                      </p>
                                    );
                                  }
                                  return (
                                    <>
                                      <p className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                                        {incorrectExplanation}
                                      </p>
                                      <div className="mt-3 flex items-center gap-3">
                                        <TTSControls
                                          text={incorrectExplanation}
                                          darkMode={darkMode}
                                          questionId={`drilldown-${q.id ?? `${area.label}-${item.index}`}-incorrect`}
                                        />
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                            {q.explanation && (
                              <div className="mt-3 text-sm">
                                <p className={`font-semibold ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                                  Explicación de la respuesta correcta
                                </p>
                                <p className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                                  {q.explanation}
                                </p>
                                <div className="mt-3 flex items-center gap-3">
                                  <TTSControls
                                    text={q.explanation}
                                    darkMode={darkMode}
                                    questionId={`drilldown-${q.id ?? `${area.label}-${item.index}`}-explanation`}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



function DetailBadge({ darkMode, title, value, accent }) {
  const accents = {
    sky: darkMode ? 'border-sky-700/40 bg-sky-400/10 text-sky-200' : 'border-sky-200 bg-sky-50 text-sky-600',
    slate: darkMode ? 'border-slate-700/40 bg-slate-900/40 text-slate-200' : 'border-slate-200 bg-white text-slate-700',
    emerald: darkMode ? 'border-emerald-700/40 bg-emerald-400/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-600',
    rose: darkMode ? 'border-rose-700/40 bg-rose-400/10 text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-600',
  };

  const classes = accents[accent] || accents.slate;

  return (
    <div className={`rounded-2xl border px-4 py-4 ${classes}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{title}</p>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
    </div>
  );
}
